import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@draftedge/ui';
import { useAuthStore } from '@/lib/auth';
import { initialsOf } from '@/lib/initials';

/**
 * Global, auth-aware top bar. Single source of truth for "are you signed in"
 * affordances so no page disagrees:
 *   - signed in  → Dashboard link + avatar (→ account) + Logout
 *   - signed out → Sign in + Get started
 *
 * Mounted once in App.tsx INSIDE the BootstrapGate, so `isBootstrapping` is
 * already resolved here — the header never flashes the wrong state. (We still
 * guard defensively.)
 *
 * Hidden on the focused draft surfaces (/drafts/new, /drafts/:id) which run
 * their own full-screen chrome.
 */
export function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isBootstrapping = useAuthStore((s) => s.isBootstrapping);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  // Keep the draft room / new-draft wizard distraction-free.
  if (/^\/drafts\/.+/.test(location.pathname)) {
    return null;
  }

  const handleLogout = async () => {
    await logout(); // optimistic — clears auth state synchronously first
    navigate('/', { replace: true });
  };

  return (
    <header className="sticky top-0 z-40 border-b border-slate-700/50 bg-surface/95 backdrop-blur supports-[backdrop-filter]:bg-surface/80">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
        <Link to="/" className="font-display text-lg font-bold tracking-tight">
          Draft<span className="text-accent">Edge</span>
        </Link>

        {/* Right cluster — never rendered until bootstrap settles. */}
        {isBootstrapping ? (
          <div className="h-9 w-24" aria-hidden="true" />
        ) : isAuthenticated ? (
          <nav className="flex items-center gap-2 sm:gap-3">
            <Link to="/rankings" className="hidden text-sm text-ink-muted hover:text-ink sm:inline">
              Rankings
            </Link>
            <Link to="/dashboard">
              <Button variant="ghost" size="sm">
                Dashboard
              </Button>
            </Link>
            <Link
              to="/account"
              title="Account settings"
              className="flex h-9 w-9 items-center justify-center rounded-pill bg-brand-primary text-xs font-semibold uppercase text-white transition hover:opacity-90"
            >
              {initialsOf(user?.first_name, user?.last_name, user?.email)}
            </Link>
            <Button variant="secondary" size="sm" onClick={handleLogout}>
              Logout
            </Button>
          </nav>
        ) : (
          <nav className="flex items-center gap-2 sm:gap-3">
            <Link to="/login">
              <Button variant="ghost" size="sm">
                Sign in
              </Button>
            </Link>
            <Link to="/register">
              <Button variant="primary" size="sm">
                Get started
              </Button>
            </Link>
          </nav>
        )}
      </div>
    </header>
  );
}
